import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, api } from "../App";
import AffiliatesPage from "./AffiliatesPage";
import AuthHeader from "../components/AuthHeader";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Loader2, UserX, ArrowRight } from "lucide-react";

/**
 * AffiliateDashboard - Protected route for logged-in affiliates.
 * Shows the affiliate dashboard with site-wide AuthHeader.
 * Shows clear error state for non-affiliates (doesn't silently bounce).
 */
const AffiliateDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [isAffiliate, setIsAffiliate] = useState(false);
  const [affiliateStatus, setAffiliateStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const checkAffiliateStatus = async () => {
      if (!user) {
        setChecking(false);
        return;
      }

      try {
        const res = await api.get("/affiliates/me");
        console.log("Dashboard - affiliate/me response:", res.data);
        
        if (res.data?.is_affiliate === true && res.data?.status === "active") {
          setIsAffiliate(true);
          setAffiliateStatus("active");
        } else {
          // Not an active affiliate - show error state
          setIsAffiliate(false);
          setAffiliateStatus(res.data?.status || "none");
          setErrorMessage(res.data?.message || "You are not registered as an affiliate.");
        }
      } catch (err) {
        console.error("Failed to check affiliate status:", err);
        setIsAffiliate(false);
        setAffiliateStatus("error");
        setErrorMessage("Failed to verify affiliate status. Please try again.");
      } finally {
        setChecking(false);
      }
    };

    if (!authLoading) {
      checkAffiliateStatus();
    }
  }, [user, authLoading]);

  // Show loading while checking auth and affiliate status
  if (authLoading || checking) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4F34A] animate-spin" />
      </div>
    );
  }

  // Not logged in - redirect to login
  if (!user) {
    navigate("/login?returnTo=/affiliates/dashboard", { replace: true });
    return null;
  }

  // Not an active affiliate - show clear error state (don't silently bounce)
  if (!isAffiliate) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <AuthHeader showDashboardLink={true} />
        <div className="pt-24 pb-12 px-6 md:px-12">
          <div className="max-w-lg mx-auto">
            <Card className="bg-surface border-[#262626]" data-testid="not-affiliate-card">
              <CardContent className="py-10">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-[#262626] flex items-center justify-center mx-auto mb-5">
                    <UserX className="w-8 h-8 text-text-muted" />
                  </div>
                  <h2 className="font-heading text-xl font-bold text-white mb-3">
                    {affiliateStatus === "pending_verification" 
                      ? "Verification Required"
                      : affiliateStatus === "pending"
                        ? "Application Pending"
                        : "Not an Active Affiliate"
                    }
                  </h2>
                  <p className="text-text-muted mb-6">
                    {errorMessage}
                  </p>
                  
                  {affiliateStatus === "pending_verification" ? (
                    <p className="text-sm text-text-muted mb-6">
                      Please sign in with Google using the email you registered with to verify your account.
                    </p>
                  ) : affiliateStatus === "pending" ? (
                    <p className="text-sm text-text-muted mb-6">
                      Your application is being reviewed. You'll receive an email once approved.
                    </p>
                  ) : null}
                  
                  <Link to="/affiliates">
                    <Button className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold">
                      {affiliateStatus === "none" || affiliateStatus === "error"
                        ? "Apply to Become an Affiliate"
                        : "Back to Affiliate Program"
                      }
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Logged in active affiliate - show dashboard with AuthHeader
  return (
    <div className="min-h-screen bg-[#050505]">
      <AuthHeader showDashboardLink={true} />
      <div className="pt-20">
        <AffiliatesPage isEmbedded={true} />
      </div>
    </div>
  );
};

export default AffiliateDashboard;
